import { useMemo, useRef } from 'react';
import type { Lens } from '@hookform/lenses';
import { Big } from 'big.js';
import { z } from 'zod/v4';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { SwitchInputGroup } from '@interxfi/design-system/components/input/switch-input/group/switch-input-group';
import { DEFAULT_PERCENTAGE_MASK } from '@interxfi/design-system/components/input/text-input/masks';
import { useIsElementVisible } from '@interxfi/design-system/util/visibility';
import { type IxcRegistrationV1BeneficialOwnerIndirect, IxcRegistrationV1ProfileStatus } from '@interxfi/types/generated/api';
import { parseNumber } from '@interxfi/utilities/number/parse';
import {
  buildProfileListQuery,
  RegistrationProfileComboboxWithLens,
} from '../../../../../../../components/data-combobox-input/entity-config/registration-profile';
import { AppSwitchInputWithLens } from '../../../../../../../components/form/app-switch-input';
import { AppTextInputWithLens } from '../../../../../../../components/form/app-text-input';
import { addDefinedValues } from '../../../../../../../util/has-value';
import { getNumericStringValidator } from '../../../../../../../util/validation';

export const indirectBeneficialOwnerControlPersonFormSchema = z.object({
  kind: z.literal('indirectBeneficialOwner'),
  value: z.object({
    profileId: z.string().nonempty('Profile is required'),
    beneficialOwnerEntity: z.string(),
    ownership: getNumericStringValidator('Ownership Percentage', { gte: 25, lte: 100, required: true }),
    passThroughEntities: z.string(),
    signer: z.boolean(),
  }),
  primaryContact: z.boolean(),
});

export type IndirectBeneficialOwnerControlPersonFormValues = z.infer<typeof indirectBeneficialOwnerControlPersonFormSchema>;

export const indirectBeneficialOwnerControlPersonFormDefaultValues = {
  kind: 'indirectBeneficialOwner',
  value: {
    profileId: '',
    beneficialOwnerEntity: '',
    ownership: '',
    passThroughEntities: '',
    signer: false,
  },
  primaryContact: false,
} as const satisfies IndirectBeneficialOwnerControlPersonFormValues;

export function formValuesToIndirectBeneficialOwnerControlPerson(
  values: IndirectBeneficialOwnerControlPersonFormValues,
): IxcRegistrationV1BeneficialOwnerIndirect {
  let ownership = 0;

  try {
    ownership = new Big(parseNumber(values.value.ownership)).div(100).toNumber();
  } catch {}

  return addDefinedValues<IxcRegistrationV1BeneficialOwnerIndirect>(
    {
      profileId: values.value.profileId,
      ownership,
      signer: values.value.signer,
    },
    {
      beneficialOwnerEntity: values.value.beneficialOwnerEntity,
      passThroughEntities: values.value.passThroughEntities,
    },
  );
}

export function getInitialFormValuesForIndirectBeneficialOwner(
  data: IxcRegistrationV1BeneficialOwnerIndirect | undefined,
  primaryContactId: string | undefined,
): IndirectBeneficialOwnerControlPersonFormValues {
  return {
    ...indirectBeneficialOwnerControlPersonFormDefaultValues,
    value: {
      ...indirectBeneficialOwnerControlPersonFormDefaultValues.value,
      profileId: data?.profileId ?? '',
      beneficialOwnerEntity: data?.beneficialOwnerEntity ?? '',
      ownership: data?.ownership ? new Big(data.ownership).mul(100).toString() : '',
      passThroughEntities: data?.passThroughEntities ?? '',
      signer: Boolean(data?.signer),
    },
    primaryContact: Boolean(data?.profileId && data.profileId === primaryContactId),
  };
}

interface IndirectBeneficialOwnerControlPersonFormProps {
  lens: Lens<IndirectBeneficialOwnerControlPersonFormValues>;
  programId: string | undefined;
}

export function IndirectBeneficialOwnerControlPersonForm({ lens, programId }: IndirectBeneficialOwnerControlPersonFormProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useIsElementVisible(ref);
  const profileSelectRequest = useMemo(() => {
    if (!programId) {
      return null;
    }

    return buildProfileListQuery({ programId, status: IxcRegistrationV1ProfileStatus.Active });
  }, [programId]);

  return (
    <VerticalStack gap={4} ref={ref}>
      <RegistrationProfileComboboxWithLens
        label="Profile"
        lens={lens.focus('value.profileId')}
        queryOptions={profileSelectRequest}
        required={isVisible}
      />

      <AppTextInputWithLens
        label="Ownership Percentage"
        lens={lens.focus('value.ownership')}
        maskOptions={DEFAULT_PERCENTAGE_MASK}
        required={isVisible}
      />

      <SwitchInputGroup>
        <AppSwitchInputWithLens label="Signer" lens={lens.focus('value.signer')} />
        <AppSwitchInputWithLens label="Primary Contact" lens={lens.focus('primaryContact')} />
      </SwitchInputGroup>

      <AppTextInputWithLens label="Beneficial Owner Entity" lens={lens.focus('value.beneficialOwnerEntity')} />

      <AppTextInputWithLens
        hint='Other entities "looked through" to identify this person'
        label="Pass-Through Entities"
        lens={lens.focus('value.passThroughEntities')}
      />
    </VerticalStack>
  );
}
